import dayjs from 'dayjs'
import i18n from '@/scripts/app-configs/i18n-config'
import { preferredLanguage } from '@/scripts/app-configs/language/languages-configs'
import systemStoragePlugin from '@/store/plugins/systemStorage'
import getLocalFiles from '@/scripts/tools/get-local-files'

const APP_VERSION_NUMBER = '1.0.1'

import { FEATURE_TOGGLE } from '@/scripts/app-configs/constants'

const appVersionStruct = () => {
  return {
    date: null,
    release: null,
    commit: null,
  }
}

const defaultSystemState = () => {
  return {
    UIReady: false,
    UILoadingMessage: 'Booting app...',
    version: {
      number: APP_VERSION_NUMBER,
      client: appVersionStruct(),
      build: appVersionStruct(),
    },
    language: preferredLanguage,
    measurementSystem: 'imperial',
    routeBeforeInactivity: {
      userId: null,
      routeFullPath: null,
    },
  }
}

const AI_FEED_USERS = [
  42295,
  75279,
  60673,
  211592,
  70459,
  76589,
  76586,
  120231,
  7239,
  8662,
  65008,
  115732,
  93470,
  9230,
  127751,
  77782,
  122783,
  75281,
  71483,
  115605,
  120233,
  65007,
  120218,
  139925,
  162350,
  159230,
  142160,
  2448,
  120233,
  250144,
  250145,
  116590,
  8692,
  88320,
  31319,
  123371,
  120198,
  120227,
  71482,
  74159,
  70462,
  39791,
  7593,
  76590,
  31320,
  2749,
  10153,
  1912,
  191784,
  196669,
  196675,
  191778,
]

export default {
  namespaced: true,
  state: {
    ...defaultSystemState(),
  },
  getters: {
    uiReady: (state) => state.UIReady,
    uiLoadingMessage: (state) => state.UILoadingMessage,
    appVersion: (state) => state.version.client,
    appVersionAnnotation: (state) => {
      const buildNumber = state.version?.client?.commit ? `(${state.version.client.commit.slice(0, 6)})` : ''

      return `v${state.version.number} ${buildNumber}`
    },
    appUpdated: (state) => {
      function formatVersionHash(versionObject) {
        return `${versionObject.commit}_${versionObject.release}`
      }
      const { build, client } = state.version

      return formatVersionHash(build) === formatVersionHash(client)
    },
    getLanguage: (state) => state.language,
    getMeasurementSystem: (state) => state.measurementSystem,
    getRouteBeforeInactivity: (state) => state.routeBeforeInactivity,
    enableAiFeed: (state, getters, rootState, rootGetters) => {
      return FEATURE_TOGGLE.UML_ENABLED && (AI_FEED_USERS.includes(rootGetters['user/getUserId']) || process.env.NODE_ENV === 'development')
    },
    defaultPage: (state, getters, rootState, rootGetters) => {
      const userExists = rootGetters['user/userExist']
      const userRole = rootGetters['user/getUserRole']

      const stateObj = {
        state: getters.enableAiFeed ? 'ai-feed' : 'patients-funnel',
        params: {},
        options: { reload: true },
      }

      if (!userExists || !userRole) {
        stateObj.state = 'login'

        return stateObj
      }

      return stateObj
    },
  },
  mutations: {
    setGlobalLoadingMessage: (state, message) => {
      state.UILoadingMessage = message
    },
    resetGlobalLoadingMessage: (state) => {
      state.UILoadingMessage = defaultSystemState().UILoadingMessage
    },
    setUIReadyState: (state, status) => {
      state.UIReady = status
    },
    changeLanguage: (state, lang) => {
      i18n.locale = lang
      state.language = i18n.locale
      dayjs.locale(i18n.locale)
    },
    changeMeasurementSystem: (state, units) => {
      state.measurementSystem = units
    },
    setAppVersion: (state, { context = 'client', versionObj = {} }) => {
      if (!state.version[context]) {
        console.error('[app-version] - Unable to set app version. Unsupported version context')

        return
      }
      state.version.number = APP_VERSION_NUMBER
      Object.keys(appVersionStruct()).forEach((key) => {
        state.version[context][key] = versionObj[key]
      })
    },
    setRouteBeforeInactivity(state, { userId, routeFullPath }) {
      state.routeBeforeInactivity.userId = userId
      state.routeBeforeInactivity.routeFullPath = routeFullPath
    },
    clearRouteBeforeInactivity(state) {
      Object.assign(state.routeBeforeInactivity, defaultSystemState().routeBeforeInactivity)
    },
  },
  actions: {
    async importPortalVersion({ commit }) {
      try {
        const { data: versionObj } = await getLocalFiles('version.json')

        commit('setAppVersion', { versionObj })

        return versionObj
      } catch (error) {
        return Promise.reject(error)
      }
    },
    resetSystem({ commit }) {
      // Remove persistent copy of this store.
      systemStoragePlugin.storage.removeItem(systemStoragePlugin.key)
      const systemDefaults = defaultSystemState()

      commit('changeMeasurementSystem', systemDefaults.measurementSystem)
    },
  },
}
